import { useMemo } from 'react';
import { Wine, GroupedHistoryEntry } from '../types';

interface StatsTabProps {
  wines: Wine[];
  groupedHistory: GroupedHistoryEntry[];
  cellarName?: string;
}

export function StatsTab({ wines, groupedHistory, cellarName }: StatsTabProps) {
  // Calculate stats
  const stats = useMemo(() => {
    const totalWines = wines.length;
    const totalBottles = wines.reduce((sum, w) => sum + w.quantity, 0);
    const totalValue = wines.reduce((sum, w) => sum + (w.purchase_price || 0) * w.quantity, 0);
    const avgPrice = totalBottles > 0 
      ? wines.reduce((sum, w) => sum + (w.purchase_price || 0) * w.quantity, 0) / totalBottles
      : 0;
    
    const bottlesDrunk = groupedHistory.reduce((sum, e) => sum + e.total_consumed, 0);
    
    // This year
    const thisYear = new Date().getFullYear();
    const thisYearBottles = groupedHistory.reduce((sum, entry) => {
      return sum + entry.occasions
        .filter(o => new Date(o.date_drank).getFullYear() === thisYear)
        .reduce((oSum, o) => oSum + o.quantity_consumed, 0);
    }, 0);
    
    return { totalWines, totalBottles, totalValue, avgPrice, bottlesDrunk, thisYearBottles };
  }, [wines, groupedHistory]);

  // Color breakdown
  const colorBreakdown = useMemo(() => {
    const byColor: Record<string, number> = {};
    wines.forEach(w => {
      const color = w.color || 'unknown';
      byColor[color] = (byColor[color] || 0) + w.quantity;
    });
    
    const total = Object.values(byColor).reduce((sum, c) => sum + c, 0);
    
    return Object.entries(byColor)
      .map(([color, count]) => ({
        color,
        count,
        percent: total > 0 ? Math.round((count / total) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count);
  }, [wines]);

  // Top varietals
  const topVarietals = useMemo(() => {
    const byVarietal: Record<string, number> = {};
    wines.forEach(w => {
      if (w.varietal) {
        byVarietal[w.varietal] = (byVarietal[w.varietal] || 0) + w.quantity;
      }
    });
    
    return Object.entries(byVarietal)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [wines]);

  const getColorValue = (color: string) => {
    switch (color.toLowerCase()) {
      case 'red': return 'var(--wine-red)';
      case 'white': return 'var(--wine-white)';
      case 'rosé': case 'rose': return 'var(--wine-rose)';
      case 'orange': return 'var(--wine-orange)';
      default: return 'var(--color-text-muted)';
    }
  };

  return (
    <div className="main-content" style={{ background: 'var(--color-parchment)' }}>
      {/* Header */}
      <div className="ios-header">
        <h1 className="ios-header-title">Stats</h1>
        <p className="ios-header-subtitle">
          {cellarName || 'Your cellar'} overview
        </p>
      </div>

      {/* Scrollable Content */}
      <div style={{
        padding: '0 16px',
        paddingBottom: '100px',
        overflowY: 'auto'
      }}>
        {/* Overview Stats */}
        <div className="ios-card" style={{
          padding: '16px',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px',
          marginBottom: '16px'
        }}>
          <div>
            <span style={{ fontSize: '13px', color: 'var(--color-text-muted)', display: 'block' }}>
              Total Wines
            </span>
            <span className="wine-title" style={{ 
              fontSize: '24px', 
              color: 'var(--color-wine)'
            }}>
              {stats.totalWines}
            </span>
          </div>
          <div>
            <span style={{ fontSize: '13px', color: 'var(--color-text-muted)', display: 'block' }}>
              Total Bottles
            </span>
            <span className="wine-title" style={{ 
              fontSize: '24px', 
              color: 'var(--color-wine)'
            }}>
              {stats.totalBottles}
            </span>
          </div>
          <div>
            <span style={{ fontSize: '13px', color: 'var(--color-text-muted)', display: 'block' }}>
              Collection Value
            </span>
            <span className="wine-title" style={{ 
              fontSize: '24px', 
              color: 'var(--color-wine)'
            }}>
              ${stats.totalValue.toLocaleString()}
            </span>
          </div>
          <div>
            <span style={{ fontSize: '13px', color: 'var(--color-text-muted)', display: 'block' }}>
              Avg. Price
            </span>
            <span className="wine-title" style={{ 
              fontSize: '24px', 
              color: 'var(--color-wine)'
            }}>
              ${Math.round(stats.avgPrice)}
            </span>
          </div>
        </div>

        {/* By Color */}
        {colorBreakdown.length > 0 && (
          <div className="ios-card" style={{
            padding: '16px',
            marginBottom: '16px'
          }}>
            <h3 style={{ 
              fontSize: '14px', 
              color: 'var(--color-text-muted)', 
              margin: '0 0 12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              By Color
            </h3>
            
            {/* Simple bar chart */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ 
                display: 'flex', 
                height: '24px', 
                borderRadius: '12px', 
                overflow: 'hidden' 
              }}>
                {colorBreakdown.map((c, i) => (
                  <div 
                    key={c.color} 
                    style={{ 
                      width: `${c.percent}%`, 
                      background: getColorValue(c.color),
                      borderRight: i < colorBreakdown.length - 1 ? '2px solid white' : 'none'
                    }} 
                  />
                ))}
              </div>
            </div>
            
            {/* Legend */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {colorBreakdown.map(c => (
                <div key={c.color} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ 
                    width: '10px', 
                    height: '10px', 
                    borderRadius: '5px', 
                    background: getColorValue(c.color),
                    border: c.color.toLowerCase() === 'white' ? '1px solid #ddd' : 'none'
                  }} />
                  <span style={{ fontSize: '14px', color: 'var(--color-text)', textTransform: 'capitalize' }}>
                    {c.color} ({c.count})
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Varietals */}
        {topVarietals.length > 0 && (
          <div className="ios-card" style={{
            padding: '16px',
            marginBottom: '16px'
          }}>
            <h3 style={{ 
              fontSize: '14px', 
              color: 'var(--color-text-muted)', 
              margin: '0 0 12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Top Varietals
            </h3>
            
            {topVarietals.map((v, index) => (
              <div key={v.name} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: index < topVarietals.length - 1 ? '10px' : 0
              }}>
                <span style={{ 
                  width: '20px', 
                  fontSize: '14px', 
                  color: 'var(--color-text-muted)',
                  fontWeight: '600'
                }}>
                  #{index + 1}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{
                    height: '20px',
                    background: 'var(--color-wine)',
                    borderRadius: '4px',
                    width: `${(v.count / topVarietals[0].count) * 100}%`,
                    minWidth: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: '8px'
                  }}>
                    <span style={{ fontSize: '12px', color: 'white', fontWeight: '600' }}>
                      {v.count}
                    </span>
                  </div>
                </div>
                <span style={{ 
                  fontSize: '14px', 
                  color: 'var(--color-text)', 
                  width: '120px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {v.name}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Drinking Stats */}
        <div className="ios-card" style={{ padding: '16px' }}>
          <h3 style={{ 
            fontSize: '14px', 
            color: 'var(--color-text-muted)', 
            margin: '0 0 12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Drinking
          </h3>
          <div style={{ display: 'flex', justifyContent: 'space-around' }}>
            <div style={{ textAlign: 'center' }}>
              <span className="wine-title" style={{ 
                fontSize: '28px', 
                color: 'var(--color-wine)',
                display: 'block'
              }}>
                {stats.bottlesDrunk}
              </span>
              <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>All time</span>
            </div>
            <div style={{ width: '1px', background: 'var(--color-border)' }} />
            <div style={{ textAlign: 'center' }}>
              <span className="wine-title" style={{ 
                fontSize: '28px', 
                color: 'var(--color-wine)',
                display: 'block'
              }}>
                {stats.thisYearBottles}
              </span>
              <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>This year</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
